<script lang="ts">
	import { replaceState } from '$app/navigation';
	import { page } from '$app/state';
	import SubNavigationAnchor from './subNavigationAnchor.svelte';
	import ScrollDetector from '$lib/scrollDetector.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import type { ButtonV3Props } from '../buttonV3.svelte';
	import type { EppoData } from '../../types';
	import type { AddToCartButtonProps } from '../cart/addToCartButton.svelte';
	import Blok from '../../blok.svelte';
	import { onMount } from 'svelte';
	import { subNavigation } from '$lib/state/subNavigation.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: string;
			subheadline: string;
			cta: Array<ISbStoryData<ButtonV3Props | EppoData | AddToCartButtonProps>['content']>;
		}>['content'];
	}

	let { content }: Props = $props();

	let subnav = $state<HTMLElement | null>(null);
	let menuEntries = $state<Array<{ name: string; id: string }>>([]);

	let heroElement: HTMLElement | null = null;
	let show = $state(false);

	function handleScroll(y: number) {
		const heroHeight = heroElement?.clientHeight || 0;
		show = y >= heroHeight;
	}

	function setScrollMargin(section: HTMLElement) {
		if (!subnav) return;

		section.style.setProperty(
			'scroll-margin-top',
			`calc(${subnav.clientHeight}px + var(--banner-height) + 1.25rem)`
		);
	}

	let activeNavElement = $state<HTMLElement | null>(null);

	onMount(() => {
		const scrollObserver = new IntersectionObserver(
			(entries) => {
				for (const { target, isIntersecting } of entries) {
					let activeAnchor = page.state.activeAnchor;

					if (isIntersecting) {
						activeAnchor = target.id;
					} else if (activeAnchor === target.id) {
						activeAnchor = null;
					}

					if (activeAnchor !== page.state.activeAnchor) {
						replaceState('', { ...page.state, activeAnchor });

						if (!activeAnchor) return;

						activeNavElement = document.querySelector(
							`a[href="#${activeAnchor}"][data-subnavitem]`
						);

						if (activeNavElement) {
							activeNavElement.scrollIntoView({ behavior: 'smooth' });
						}
					}
				}
			},
			{ rootMargin: '-25% 0px -55% 0px' }
		);

		document.querySelectorAll('section.content-section').forEach((section) => {
			setScrollMargin(section as HTMLElement);

			scrollObserver.observe(section);
			menuEntries.push({ name: section.getAttribute('data-nav-name') || '', id: section.id });
		});
		menuEntries = menuEntries;

		heroElement = document.getElementById('hero');

		return () => scrollObserver.disconnect();
	});

	$effect(() => {
		subNavigation.available = subnav !== null;

		if (subnav) {
			document.querySelectorAll('section.content-section').forEach((section) => {
				setScrollMargin(section as HTMLElement);
			});
		}

		return () => {
			subNavigation.available = false;
		};
	});
</script>

<ScrollDetector onscroll={handleScroll} />

<nav
	id="mill-sub-navigation"
	use:editable={content}
	class={[
		'scrollbar-hide fixed inset-x-0 top-0 z-50 flex scroll-px-5 items-center justify-between gap-x-5 overflow-x-auto bg-white px-5 transition-transform duration-400 lg:m-5 lg:w-[calc(100vw-2.5rem)] lg:scroll-pl-6 lg:scroll-pr-5 lg:rounded-xl lg:pl-6 lg:pr-5 lg:transition-opacity',
		{
			'max-lg:-translate-y-full max-lg:transform-gpu lg:pointer-events-none lg:opacity-0': !show
		}
	]}
	bind:this={subnav}
>
	<div class="flex w-full flex-col py-3 max-lg:hidden">
		<p class="text-[20px]/[110%] font-semibold tracking-[-.2px] text-black">{content.headline}</p>

		<p class="text-balance text-[14px]/[160%] text-brown-4">{content.subheadline}</p>
	</div>
	<div class="flex items-center justify-end lg:w-full lg:gap-x-8">
		<div class="relative flex gap-x-5">
			{#each menuEntries as entry}
				<SubNavigationAnchor anchor={entry.id} active={entry.id === page.state.activeAnchor}>
					{entry.name}
				</SubNavigationAnchor>
			{/each}
			<div
				class="absolute bottom-0 left-0 h-0.5 bg-brown-1 transition-all duration-300 ease-in-out lg:h-[3px]"
				style="width: {activeNavElement?.clientWidth ??
					0}px; transform-origin: left; transform: translate3d({activeNavElement?.offsetLeft ??
					0}px,0,0)"
			></div>
		</div>
		<div class="py-3 max-lg:hidden">
			{#each content.cta as cta}
				<Blok content={cta} />
			{/each}
		</div>
	</div>
</nav>

<style lang="scss">
	nav {
		box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.06);
		margin-top: var(--banner-height);

		@media (min-width: 1024px) {
			box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.12);
			margin-top: calc(var(--banner-height) + theme('spacing.5'));
		}
	}
</style>
